import React, { useEffect, useMemo, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AccordionDetails from '@material-ui/core/AccordionActions';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { navigate } from 'gatsby';
import {
  ViewBillDetails,
  AccountDetail,
  ViewBillMonthlyUsage,
  ViewBillAverageTemperature,
  QuickViewBillInfo,
  BillInfo,
  AutoPayInfo,
  PaymentPlanType,
  PgeEnergyTrackerData,
  AccountType,
} from '../../__generated__/pge-types';
import PGEButton from '../buttons';
import colors from '../../themes/main-colors';
import { AccountParamsType } from '../../hooks/useViewBill';
import UsageGraph from './usage-graph';
import { useTranslation } from '../../hooks/useTranslation';
import Backdrop from '../backdrop';
import ROUTES from '../../routes';
import {
  toDateString,
  toCurrencyString,
  toSentenceCase,
  dateSuffixMap,
} from '../../util/format';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ViewRecentPayments from './view-recent-payments';
import SpinnerCard from '../spinner/SpinnerCard';
import { Card, CardContent } from '@material-ui/core';
import ErrorOverlay from '../error/error-overlay';
import IconMoney from '../../../src/static/MoneyIcon.svg';
import IconChartenergy from '../../../src/static/ChartenergyIcon.svg';
import { TpaAccountDetails } from '../need-more-time-to-pay/tpa/useTPA';
import useAuth from '../../hooks/useAuth';
import BillingOptionsIcon from '../../static/billingOptions.svg';
import BillSummary from '../account-summary/bill-summary';
import { getBillingOptionsDetails } from '../account-summary/single-account/queries';
import useAuthQuery from '../../hooks/useAuthQuery';
import { Option } from '../account-summary/toggleable-options-list/ToggleableOptionsList';
import ProgramOptionsList from '../account-summary/program-options-list/ProgramOptionsList';
import { useLocation } from '@reach/router';
import usePgeQuery from '../../hooks/usePgeQuery';
import { QuickViewBillQuery } from '../../queries/getQuickViewBillInfo.query';
import TEST_IDS from '../../constants/test_ids';
import { useIsMobile } from '../../util/style-utils';
import WidgetHost from '../cs-widgets/WidgetHost';
import useAccountCustomer from '../../hooks/useAccountCustomer';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    rootNegativeMargin: {
      color: colors.noirBlur,
      paddingLeft: 56,
      paddingRight: 56,
      '& .MuiButton-endIcon': {
        marginRight: -280,
        marginLeft: 280,
      },
    },
    testButton: {
      color: colors.noirBlur,
      '&:hover': {
        color: '#FFF',
      },
      '& .MuiButton-label': {
        '& .MuiButton-endIcon': {
          marginLeft: '0px',
        },
      },
    },
    centerColumn: {
      width: '100%',
    },
    header: {
      margin: theme.spacing(1, 2, 2, 6),
      color: colors.noirBlur,
      fontWeight: 800,
    },
    accountCard: {
      margin: theme.spacing(2, 0, 0, 0),
      width: '100%',
    },
    adCard: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: '1rem',
      height: '95%',
    },
    text: {
      margin: theme.spacing(1, 2, 2, 2),
      color: colors.noirBlur,
    },
    textLeft: {
      margin: theme.spacing(1, 6, 2, 2),
      color: colors.noirBlur,
    },
    billText: {
      margin: theme.spacing(2, 0, 0, 0),
      color: colors.noirBlur,
    },
    billTextBottom: {
      margin: theme.spacing(2, 0, 2, 0),
      color: colors.noirBlur,
    },
    billTextMoney: {
      margin: theme.spacing(2, 0, 0, 0),
      fontWeight: 'bold',
    },
    buttonBox: {
      margin: theme.spacing(2, 0, 2, 0),
    },
    viewDetails: {
      margin: theme.spacing(0),
      color: colors.noirBlur,
      width: '100%',
    },
    hdivider: {
      margin: theme.spacing(1, 2, 0, 2),
    },
    hdivider2: {
      margin: theme.spacing(3, 1, 3, 1),
    },
    vdivider: {
      margin: theme.spacing(1, 0, 0, 0),
    },
    dolla: {
      margin: theme.spacing(2, 0, 2, 2),
      color: colors.noirBlur,
    },
    expander: {
      margin: theme.spacing(0, 0, 0, 4),
      color: colors.noirBlur,
    },
    viewDetailsGrid: {
      margin: theme.spacing(0, 10),
    },
    pdfBill: {
      margin: theme.spacing(4, 0, 0, 0),
    },
    buttonText: {
      color: colors.noirBlur,
    },
    graph: {
      margin: theme.spacing(2, 0, 0, 0),
    },
    comparisonTextLeft: {
      margin: theme.spacing(2, 2, 2, 6),
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(1),
        fontSize: '0.8rem',
      },
    },
    comparisonTextCenter: {
      margin: theme.spacing(2, 2, 2, 2),
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(1),
        fontSize: '0.8rem',
      },
    },
    comparisonTextRight: {
      margin: theme.spacing(2, 6, 2, 2),
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(1),
        fontSize: '0.8rem',
      },
    },
    navLinks: {
      margin: theme.spacing(2, 0, 0, 0),
      '& a': {
        textDecoration: 'none',
      },
    },
    noBills: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2),
    },
    noBillFound: {
      backgroundColor: colors.roseRed,
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
      display: 'flex',
      alignItems: 'center',
      borderRadius: 4,
    },
    link: {
      textDecoration: 'none',
    },
    billLinks: {
      display: 'flex',
      flexDirection: 'column',
    },
    amountOverDueAlert: {
      maxWidth: '300px',
      color: colors.errorRed,
      fontWeight: 600,
    },
    chevronRightIconStyles: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      color: colors.noirBlur,
      marginTop: '0.5rem',
      alignItems: 'center',
    },
    homeEnergyAnalysisCard: {
      marginTop: '2rem',
      marginBottom: '0.5rem',
    },
    homeEnergyAnalysisContent: {
      display: 'flex',
      width: '75%',
    },
    homeEnergyAnalysisContentMob: {
      display: 'flex',
      paddingBottom: '0px !important',
    },
    getStartedPGEButton: {
      marginTop: '2rem',
    },
    getStartedPGEButtonMob: {
      textAlign: 'center',
      paddingTop: '0px !important',
    },
    accordianTitle: {
      fontWeight: 'bold',
      marginBottom: '1em',
      color: colors.sparkBlue,
    },
    NextBestActionWidgetStyling: {
      color: colors.noirBlur,
      marginTop: '2rem',
      height: 'auto',
    },
    accordianSubTitle: {
      '& p': {
        margin: 0,
      },
    },
    expansionCardInfo: {
      [theme.breakpoints.up('sm')]: {
        maxWidth: '70%',
      },
    },
  }),
);

type ViewBillProps = {
  accountParams: AccountParamsType | null | undefined;
  loading: boolean;
  billDetails: ViewBillDetails | null | undefined;
  selectedAccount: AccountDetail | null | undefined;
  monthUsage?: ViewBillMonthlyUsage | null | undefined;
  averageTemperature?: ViewBillAverageTemperature | null | undefined;
  quickSelectedAccount?: QuickViewBillInfo | null | undefined;
  latestPaymentHistory?: any;
  billInfo: BillInfo | null | undefined;
  autopay: AutoPayInfo | null | undefined;
  isPendingDisconnect: boolean | undefined;
  paymentPlanType: PaymentPlanType | null | undefined;
  encryptedAccountNumber: string | null | undefined;
  encryptedPersonId: string | undefined | null;
  tpaDetails?: TpaAccountDetails | undefined;
  pgeEnergyTracker?: PgeEnergyTrackerData | undefined | null;
  isSummaryBillAccount?: boolean | undefined;
  isNewAccount: boolean | null | undefined;
};
const getBillingAndPaymentOptions = (
  selectedAccount: AccountDetail | QuickViewBillInfo,
  autoPayDetails: AutoPayInfo,
  equalpay: PaymentPlanType,
  t: (translation: string) => string,
): Option[] => {
  const paymentOptions: Option[] = [];
  if (selectedAccount) {
    const {
      isPaperlessBillEnrolled,
      preferredDueDate,
      accountType,
    } = selectedAccount;
    const autoPay: Option = {
      name: t('AUTO_PAY'),
      active: Boolean(autoPayDetails?.isEnrolled),
      link: ROUTES.AUTO_PAY,
      subText: t('AUTO_PAY_PROGRAM_SUBTEXT'),
    };
    const paperlessBilling: Option = {
      name: t('PAPERLESS_BILL'),
      active: isPaperlessBillEnrolled?.result ?? false,
      link: ROUTES.PAPERLESS_BILLING,
      subText: t('PAPERLESS_PROGRAM_SUBTEXT'),
    };
    const preferredDueDateActive = Boolean(
      preferredDueDate?.dueDate?.preferredDueDate,
    );
    const preferredDueDateOption: Option = {
      name: preferredDueDateActive
        ? `${t('PREFERRED_DUE_DATE')}${preferredDueDateActive ? ': ' : ''}`
        : t('PDD_PAGE_TITLE'),
      active: preferredDueDateActive,
      data: {
        value: preferredDueDateActive
          ? `${preferredDueDate?.dueDate?.preferredDueDate}${dateSuffixMap(
              t,
              preferredDueDate?.dueDate?.preferredDueDate,
            )}`
          : '',
      },
      link: ROUTES.PREFERRED_DUE_DAY,
      subText: t('DUEDATE_PROGRAM_SUBTEXT'),
    };
    const paymentType: Option = {
      name: t('EQUAL_PAY_TITLE'),
      active: equalpay === 'EqualPay',
      disabled: false,
      link: ROUTES.EQUAL_PAY,
      subText: t('EQUAL_PAY_PROGRAM_SUBTEXT'),
    };
    paymentOptions.push(
      autoPay,
      ...(accountType === AccountType.Res ? [paymentType] : []),
      paperlessBilling,
      preferredDueDateOption,
    );
  }
  return [...paymentOptions];
};

function getToken(querySearchParam: string): string {
  const x = querySearchParam.split('request=', 2);
  return x.length === 2 ? x[1] : '';
}

const ResidentialViewBill = ({
  accountParams,
  loading,
  billDetails,
  selectedAccount,
  monthUsage,
  averageTemperature,
  quickSelectedAccount,
  latestPaymentHistory,
  billInfo,
  autopay,
  isPendingDisconnect,
  paymentPlanType,
  encryptedAccountNumber,
  encryptedPersonId,
  pgeEnergyTracker,
  isSummaryBillAccount,
  tpaDetails,
  isNewAccount,
}: ViewBillProps) => {
  const { isAuthenticated } = useAuth();
  const {
    loading: billingOptionLoading,
    data: billingOptionsData,
  } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getBillingOptionsDetails, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    errorPolicy: 'all',
    skip: !accountParams,
  });

  const token = useMemo(() => {
    return typeof window !== 'undefined'
      ? getToken(window.location.search)
      : '';
  }, []);

  const { data, loading: loading1, error } = usePgeQuery<{
    getQuickViewBillInfo: QuickViewBillInfo;
  }>(QuickViewBillQuery, {
    variables: {
      token: token,
    },
    skip: !token,
    errorPolicy: 'all', // stop it going to default error page
  });
  const getViewBillInfo = data?.getQuickViewBillInfo;
  const selectedAccountQuickViewBill = {
    accountType: getViewBillInfo?.accountType,
    isPaperlessBillEnrolled: getViewBillInfo?.isPaperlessBillEnrolled,
    preferredDueDate: getViewBillInfo?.preferredDueDate,
    paymentPlanType: getViewBillInfo?.equalpay?.paymentPlanType,
    autopay: getViewBillInfo?.autoPay,
  };
  const classes = useStyles();
  const { t, richT } = useTranslation();
  const isMobile = useIsMobile();
  const { customer, loading: customerLoading } = useAccountCustomer();
  const [isBillComparisionExpanded, setIsBillComparisionExpanded] = useState(
    true,
  );
  const location = useLocation();
  const currentPath = location.pathname;
  const [
    isBillRecentPaymentsExpanded,
    setIsBillRecentPaymentsExpanded,
  ] = useState(false);
  const [isBillingsOptionsExpanded, setIsBillingsOptionsExpanded] = useState(
    false,
  );
  const accountNumber =
    accountParams?.accountNumber ||
    selectedAccount?.accountNumber ||
    quickSelectedAccount?.accountNumber;
  const billingOptionsList = getBillingAndPaymentOptions(
    currentPath === ROUTES.QUICK_VIEWBILL
      ? (selectedAccountQuickViewBill as QuickViewBillInfo)
      : billingOptionsData?.getAccountDetails?.[0]!,
    autopay!,
    paymentPlanType!,
    t,
  );

  const accountType =
    selectedAccount?.accountType || quickSelectedAccount?.accountType;

  const truncateLength = 50;
  const formattedOptions = billingOptionsList
    ? billingOptionsList
        .filter(option => option.active)
        .map(opt => opt.name)
        .join(', ')
        .replace(/:/g, '')
    : null;

  const activeOptionsDisplay =
    formattedOptions && formattedOptions.length > truncateLength
      ? formattedOptions
          .substring(0, truncateLength)
          .replace(/,$|\s$/, '')
          .concat('...')
      : formattedOptions;

  useEffect(() => {
    if (location.hash === '#energyUse' && typeof window !== undefined) {
      document.getElementById('energyUse')?.scrollIntoView();
    }
  }, []);

  useEffect(() => {
    //Focusing the energy tracker widget with panel open.
    if (
      !loading &&
      location.hash === '#energyTracker' &&
      typeof window !== 'undefined'
    ) {
      setIsBillComparisionExpanded(true);
      document.getElementById('energyTracker')?.scrollIntoView();
    }
  }, [loading]);

  const energyTrackerLoading = !pgeEnergyTracker;
  const pgeEnergyTrackerData = pgeEnergyTracker ? pgeEnergyTracker : undefined;

  const showEnergyTrackerWidget =
    pgeEnergyTrackerData?.billableAccountDetails?.isAccountBillable &&
    pgeEnergyTrackerData?.billableAccountDetails
      ?.isServiceDesignationElectric &&
    pgeEnergyTrackerData?.energyTrackerInfo?.showEnergyTracker;

  const energyTrackerDataProps = {
    pgeEnergyTrackerData: pgeEnergyTrackerData,
    encryptedAccountNumber: encryptedAccountNumber,
    encryptedPersonId: encryptedPersonId,
    showHeader: false,
    currentPath: currentPath,
  };
  if (
    billDetails === undefined ||
    loading ||
    latestPaymentHistory === undefined
  ) {
    return <Backdrop forceOpen />;
  }

  return (
    <>
      <Paper
        className={classes.accountCard}
        data-testid={TEST_IDS.VIEW_BILL_CURRENT_BILL_CARD}
      >
        <Box justifyContent={'center'} display={'flex'}>
          <BillSummary
            billInfo={billInfo || null}
            autoPay={autopay || null}
            isPendingDisconnect={isPendingDisconnect}
            billDetails={billDetails || null}
            isSummaryBillAccount={isSummaryBillAccount}
            tpaDetails={tpaDetails}
            isNewAccount={isNewAccount}
          />
        </Box>
      </Paper>

      <Paper
        className={classes.accountCard}
        data-testid={TEST_IDS.VIEW_BILL_BILL_COMPARISON_CARD}
      >
        <Accordion expanded={isBillComparisionExpanded}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            onClick={() => {
              setIsBillComparisionExpanded(!isBillComparisionExpanded);
              if (!isAuthenticated && showEnergyTrackerWidget) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                navigate(ROUTES.SIGN_IN, {
                  state: {
                    redirectUrl:
                      ROUTES.VIEW_BILL +
                      '?selectedAccount=' +
                      accountNumber +
                      '#energyTracker',
                  },
                });
              }
            }}
          >
            <Grid
              container
              direction="row"
              spacing={3}
              style={isMobile ? { flexWrap: 'nowrap' } : {}}
            >
              <Grid item>
                <IconChartenergy />
              </Grid>
              <Grid item className={classes.expansionCardInfo}>
                <a id="energyTracker" />
                <Typography
                  className={classes.accordianTitle}
                  component={'span'}
                  variant={'h6'}
                >
                  {t('BILL_COMPARISON_TITLE')}
                </Typography>
                {!isBillComparisionExpanded && (
                  <Typography variant={'body2'}>
                    {t('VIEW_BILL_ENERGY_CARD_SUBTITLE')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Grid
              item
              xs={12}
              style={{ padding: '0px' }}
              data-testid="energy-card-with-energy-tracker"
            >
              <a id="energyUse" />
              {!customerLoading && (
                <WidgetHost
                  key={accountNumber ?? ''}
                  type="bill-comparison"
                  identifier={accountNumber ?? ''}
                  personId={customer?.personId ?? ''}
                  isMobile={isMobile}
                />
              )}
            </Grid>
            {/* {!showEnergyTrackerWidget && Boolean(monthUsage) ? (
              <Grid container>
                <Grid container>
                  <Grid container justify={'center'} alignContent={'center'}>
                    <Typography variant={'h2'} className={classes.billText}>
                      {t('ENERGY_USE')}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    justify={'center'}
                    alignContent={'center'}
                    className={classes.graph}
                  >
                    <UsageGraph monthUsage={monthUsage} isMobile={isMobile} />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container justify={'center'} alignContent={'center'}>
                  <Typography variant={'h2'} className={classes.billText}>
                    {t('COMPARISONS')}
                  </Typography>
                </Grid>
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  alignItems={'baseline'}
                  style={{ width: '100%', padding: 10 }}
                >
                  <Box>
                    <Typography
                      variant={'body1'}
                      style={{ fontWeight: 800 }}
                      className={classes.comparisonTextLeft}
                    >
                      {t('PERIOD_ENDING')}
                    </Typography>
                    <Typography
                      variant={'body1'}
                      className={classes.comparisonTextLeft}
                    >
                      {toDateString(
                        averageTemperature?.currentBillingPeriod?.date,
                        'MMM. yyyy',
                      )}
                    </Typography>
                    <Typography
                      variant={'body1'}
                      className={classes.comparisonTextLeft}
                    >
                      {toDateString(
                        averageTemperature?.previousBillingPeriod?.date,
                        'MMM. yyyy',
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant={'body1'}
                      style={{ fontWeight: 800 }}
                      className={classes.comparisonTextCenter}
                    >
                      {t('AVG_TEMP')}
                    </Typography>
                    {Boolean(
                      averageTemperature?.currentBillingPeriod
                        ?.averageTemperature,
                    ) ? (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        {Math.round(
                          averageTemperature?.currentBillingPeriod
                            ?.averageTemperature!,
                        )}
                        *
                      </Typography>
                    ) : (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        N/A
                      </Typography>
                    )}
                    {Boolean(
                      averageTemperature?.previousBillingPeriod
                        ?.averageTemperature,
                    ) ? (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        {Math.round(
                          averageTemperature?.previousBillingPeriod
                            ?.averageTemperature!,
                        )}
                        *
                      </Typography>
                    ) : (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        N/A
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography
                      variant={'body1'}
                      style={{ fontWeight: 800 }}
                      className={classes.comparisonTextCenter}
                    >
                      {t('MONTHLY_KWH')}
                    </Typography>
                    {Boolean(
                      averageTemperature?.currentBillingPeriod?.totalKwh,
                    ) ? (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        {averageTemperature?.currentBillingPeriod?.totalKwh}{' '}
                      </Typography>
                    ) : (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        N/A
                      </Typography>
                    )}
                    {Boolean(
                      averageTemperature?.previousBillingPeriod?.totalKwh,
                    ) ? (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        {averageTemperature?.previousBillingPeriod?.totalKwh}{' '}
                      </Typography>
                    ) : (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        N/A
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography
                      variant={'body1'}
                      style={{ fontWeight: 800 }}
                      className={classes.comparisonTextRight}
                    >
                      {t('MONTHLY_COST')}
                    </Typography>
                    {averageTemperature?.currentBillingPeriod?.totalCost ? (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        $
                        {toCurrencyString(
                          averageTemperature?.currentBillingPeriod?.totalCost ||
                            0,
                          false,
                        )}{' '}
                      </Typography>
                    ) : (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        N/A
                      </Typography>
                    )}
                    {averageTemperature?.previousBillingPeriod?.totalCost ? (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        $
                        {toCurrencyString(
                          averageTemperature?.previousBillingPeriod
                            ?.totalCost || 0,
                          false,
                        )}{' '}
                      </Typography>
                    ) : (
                      <Typography
                        variant={'body1'}
                        className={classes.comparisonTextCenter}
                      >
                        N/A
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant={'body1'}
                  className={classes.comparisonTextLeft}
                >
                  {`*${t('TEMP_SOURCE')} ${
                    averageTemperature?.temperatureSource
                  }`}
                </Typography>
              </Grid>
            ) : null} */}
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper className={classes.accountCard} data-testid="recent-payment-card">
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            onClick={() => {
              setIsBillRecentPaymentsExpanded(!isBillRecentPaymentsExpanded);
            }}
          >
            <Grid
              container
              direction="row"
              spacing={3}
              style={isMobile ? { flexWrap: 'nowrap' } : {}}
            >
              <Grid item>
                <IconMoney />
              </Grid>
              <Grid item>
                <Typography
                  className={classes.accordianTitle}
                  component={'span'}
                  variant={'h6'}
                >
                  {t('VIEW_BILL_RECENT_PAYMENTS_TITLE')}
                </Typography>
                {!isBillRecentPaymentsExpanded && (
                  <Typography variant={'body2'}>
                    {t('VIEW_BILL_RECENT_PAYMENTS_SUBTITLE')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Grid item xs={12}>
              <ViewRecentPayments
                lastPaymentsDetails={latestPaymentHistory}
              ></ViewRecentPayments>
              <Divider />
              <Box justifyContent={'center'} display={'flex'}>
                <PGEButton
                  variant={'outlined'}
                  onClick={() => navigate(ROUTES.PAYMENT_HISTORY)}
                  style={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 18,
                  }}
                  size={'large'}
                  className={classes.testButton}
                  endIcon={<ChevronRightIcon />}
                >
                  {t('BILLING_PAYMENT')}
                </PGEButton>
              </Box>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper
        className={classes.accountCard}
        data-testid="my-billing-options-card"
      >
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            onClick={() => {
              setIsBillingsOptionsExpanded(!isBillingsOptionsExpanded);
            }}
          >
            <Grid
              container
              direction="row"
              spacing={3}
              style={isMobile ? { flexWrap: 'nowrap' } : {}}
            >
              <Grid item>
                <BillingOptionsIcon style={{ width: '50px' }} />
              </Grid>
              <Grid item>
                <Typography
                  className={classes.accordianTitle}
                  component={'span'}
                  variant={'h6'}
                >
                  {t('MY_BILLING_OPTIONS')}
                </Typography>
                <Typography
                  variant={'body2'}
                  className={classes.accordianSubTitle}
                >
                  {billingOptionsList?.some(
                    item => item.active && !item.disabled,
                  )
                    ? richT('SELECTED_OPTION_LIST', {
                        OPTION_LIST: activeOptionsDisplay || '',
                      })
                    : richT('SELECT_YOUR_BILLING_OPTIONS')}
                </Typography>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            {billingOptionLoading ? (
              <SpinnerCard />
            ) : (
              <ProgramOptionsList
                options={billingOptionsList}
                renderInCard={false}
                isMobile={isMobile}
              />
            )}
          </AccordionDetails>
        </Accordion>
      </Paper>
    </>
  );
};

export default ResidentialViewBill;
